import React from 'react'
import { Sticky } from 'react-stickup-ay'
import styled from 'styled-components'

import StaticSplitSection from 'uiComponents/atoms/Sections/StaticSplitSection'
import RecoHeadlineGroup from 'uiComponents/molecules/RecoHeadlineGroup/RecoHeadlineGroup'
import RecoProductGrid from 'uiComponents/molecules/RecoProductGrid/RecoProductGrid'
import FullHeightVerticalSlider from 'uiComponents/molecules/Slider/FullHeightVerticalSlider'

const hoverTransitionTiming = '500ms ease'

const StyledRecoProductGrid = styled(RecoProductGrid)`
  margin-top: 50px;
`

const StyledRecoHeadlineGroup = styled(RecoHeadlineGroup)`
  min-height: 100vh;
`

const TopRightContent = styled(({ component, ...props }) =>
  React.cloneElement(component, props),
)`
  position: absolute;
  top: 15px;
  right: 15px;
  z-index: 2;
  opacity: 0;
  transition: opacity ${hoverTransitionTiming};
`

const HoverWrap = styled.div`
  &:hover {
    ${TopRightContent} {
      opacity: 1;
    }
  }
`

interface Props {
  outfitProducts: React.ReactNode[]
  stickyRef?: React.RefObject<HTMLDivElement>
  sliderImgSources: string[]
  shifted?: boolean
  title?: React.ReactNode
  subTitle?: string | null
  description?: React.ReactNode
  StartWrapper?: React.ComponentType<React.PropsWithChildren>
  ProductGridTrackingWrapper?:
    | React.FC
    | React.ComponentType<{ headline: string | undefined | null }>
  topRightContent?: React.ReactNode
}

const OutfitReco: React.FC<Props> = ({
  outfitProducts,
  stickyRef,
  sliderImgSources,
  shifted,
  title,
  subTitle,
  description,
  StartWrapper = React.Fragment,
  ProductGridTrackingWrapper = React.Fragment,
  topRightContent,
}) => {
  const slider = (
    <StartWrapper>
      {stickyRef ? (
        <Sticky container={stickyRef} experimentalNative>
          {() => (
            <HoverWrap>
              <FullHeightVerticalSlider
                scrollable={false}
                images={sliderImgSources}
              />
              {topRightContent && (
                <TopRightContent component={topRightContent} />
              )}
            </HoverWrap>
          )}
        </Sticky>
      ) : (
        <>
          <HoverWrap>
            <FullHeightVerticalSlider images={sliderImgSources} />
            {topRightContent && <TopRightContent component={topRightContent} />}
          </HoverWrap>
        </>
      )}
    </StartWrapper>
  )

  const productGrid = (
    <ProductGridTrackingWrapper headline={subTitle}>
      <StyledRecoHeadlineGroup
        dataTestId="OutfitRecoHeadlineGroup"
        title={title}
        subTitle={subTitle}
      >
        {description}
        <StyledRecoProductGrid productTiles={outfitProducts} />
      </StyledRecoHeadlineGroup>
    </ProductGridTrackingWrapper>
  )

  return (
    <StaticSplitSection
      dataTestId="OutfitReco"
      contentStart={shifted ? productGrid : slider}
      pullStart={!shifted}
      pullEnd={shifted}
      contentEnd={shifted ? slider : productGrid}
    />
  )
}

export default OutfitReco
